import React, { useState } from 'react';
import { FleetSensor } from '../types';
import { Owner, createOwner, deleteSensor } from '../services/adminService';
import { getProductInfo } from '../services/productCatalog';
import { X, Plus, Trash2 } from 'lucide-react';

interface Props {
  sensor: FleetSensor;
  owners: Owner[];
  onClose: () => void;
  onSaved: () => void;
  onSave: (
    sensorId: string, alias: string, userId: string | null, location: string | null,
    status: string | null, notes: string | null,
  ) => Promise<boolean>;
}

const NEW_OWNER = '__new__';

// Assigning a sensor: who owns it and where it is. device_id and role are not
// editable — device_id is the device's identity, bound to a board's MAC
// server-side, so retyping it would hand one device another's history.
//
// The product name is shown but not editable: every unit of a product carries
// the same one, and units are told apart by device_id. What you can set is an
// alias — optional, and only if this customer wants their own label for it.
const SensorEditModal: React.FC<Props> = ({ sensor, owners, onClose, onSaved, onSave }) => {
  const product = getProductInfo(sensor.type);

  const [alias, setAlias] = useState(sensor.alias ?? '');
  const [userId, setUserId] = useState(sensor.userId ?? '');
  const [location, setLocation] = useState(sensor.location ?? '');
  const [status, setStatus] = useState(sensor.status ?? 'deployed');
  const [notes, setNotes] = useState(sensor.notes ?? '');
  const [newOwnerName, setNewOwnerName] = useState('');
  const [newOwnerPin, setNewOwnerPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const creatingOwner = userId === NEW_OWNER;

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    let targetUserId: string | null = userId || null;

    if (creatingOwner) {
      if (!/^\d{4}$/.test(newOwnerPin)) {
        setError('The PIN must be exactly 4 digits.');
        setSaving(false);
        return;
      }
      const created = await createOwner(newOwnerName, newOwnerPin);
      if (!created) {
        setError('Could not create the customer. The name may already be taken.');
        setSaving(false);
        return;
      }
      targetUserId = created.id;
    }

    const ok = await onSave(sensor.sensorId, alias, targetUserId, location, status, notes);
    setSaving(false);
    if (ok) onSaved();
    else setError('Could not save. Your admin session may have expired — log out and back in.');
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-100">Assign sensor</h2>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">{sensor.deviceId ?? '—'}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Product</label>
        <div className="w-full bg-slate-900/60 border border-slate-700/60 text-slate-400 rounded-lg px-3 py-2 mb-4">
          {product?.name ?? sensor.sensorName}
        </div>

        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
          Alias <span className="normal-case tracking-normal text-slate-600">(optional)</span>
        </label>
        <input
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2"
          placeholder="What the customer calls this one"
        />
        <p className="text-xs text-slate-500 mt-1 mb-4">
          Leave empty to show the product name. Units are told apart by the device id above.
        </p>

        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Customer</label>
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 mb-4"
        >
          <option value="">— unchanged —</option>
          {owners.filter(o => !o.archivedAt).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          <option value={NEW_OWNER}>+ New customer…</option>
        </select>

        {creatingOwner && (
          <div className="border border-slate-700 rounded-lg p-3 mb-4 bg-slate-900/50">
            <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
              <Plus className="w-3.5 h-3.5" /> New customer
            </div>

            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Customer name</label>
            <input
              value={newOwnerName}
              onChange={(e) => setNewOwnerName(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 mb-3"
              placeholder="e.g. Hotel Marbella"
            />

            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Dashboard PIN</label>
            <input
              value={newOwnerPin}
              onChange={(e) => setNewOwnerPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric"
              className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2"
              placeholder="4 digits"
            />
            <p className="text-xs text-slate-500 mt-1">
              Their private dashboard link is generated automatically — you'll see it in the table once saved.
            </p>
          </div>
        )}

        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Location</label>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 mb-4"
          placeholder="e.g. Roof tank, main line"
        />

        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 mb-4"
        >
          <option value="bench">bench — in development</option>
          <option value="spare">spare — built, unassigned</option>
          <option value="deployed">deployed — in the field</option>
          <option value="rma">rma — returned / faulty</option>
          <option value="retired">retired</option>
        </select>

        <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">
          Notes <span className="normal-case tracking-normal text-slate-600">(optional)</span>
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 mb-4"
          placeholder="Board swapped 2026-11-03, previous mac AA:BB:... (water damage, RMA)"
        />
        {sensor.mac && (
          <p className="text-xs text-slate-600 -mt-2 mb-4 font-mono">board mac: {sensor.mac}</p>
        )}

        {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

        <div className="flex items-center gap-2">
          {sensor.role !== 'node' ? (
            // Gateways and telemetry identities can't be recreated: their
            // ingest_key lives only in the database, and deleting a gateway
            // also silences every node that relays through it.
            <span className="mr-auto text-xs text-slate-600" title="Only nodes can be deleted">
              {sensor.role === 'gateway' ? 'Gateways' : 'Telemetry identities'} can't be deleted here
            </span>
          ) : confirmingDelete ? (
            <div className="flex items-center gap-2 mr-auto">
              <span className="text-xs text-red-400">Delete this device and all its readings?</span>
              <button
                onClick={async () => {
                  setSaving(true);
                  const message = await deleteSensor(sensor.sensorId);
                  setSaving(false);
                  if (message) setError(message); else onSaved();
                }}
                className="px-2 py-1 text-xs bg-red-600 hover:bg-red-500 text-white rounded"
              >
                Yes, delete
              </button>
              <button onClick={() => setConfirmingDelete(false)} className="text-xs text-slate-400 hover:text-slate-200">
                No
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="mr-auto inline-flex items-center gap-1.5 px-2 py-2 text-sm text-slate-500 hover:text-red-400"
              title="Delete this device"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-slate-200">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || (creatingOwner && !newOwnerName.trim())}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-lg font-medium"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SensorEditModal;
